import { Box, FormHelperText, Grid2 } from '@mui/material';
import Title from 'modules/Pricing/components/Title';
import { Field, Form, FormSpy } from 'react-final-form';
import ActionButtons from '../ActionButtons';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { useEffect, useMemo, useState } from 'react';
import { UnitType } from '@utriper/core';
import { useTranslation } from 'react-i18next';
import { DataGrid, GroupedSelect } from '@utriper/core-ui';
import { Label } from '../../styles';

const AddUnitForm = ({ priceGroupId, handleClose }) => {
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`price_group_id.add_unit_form.${key}`);
    const { addUnit, startColumn, unitsPriceGroup, units, prices: rangePrices, submittingPriceGroup, validatePrices } = usePriceGroup();
    const unitsOptions = useMemo(() => {
        const options = units?.map((unit) => {
            if (unitsPriceGroup.some((unitPriceGroup) => unitPriceGroup?.unitType === unit?.typeId)) return false;

            return {
                id: unit.id,
                name: unit?.name,
                group: UnitType.getTranslatedName(UnitType.types, unit?.typeId, i18n.language),
            };
        });

        return options.filter(Boolean);
    }, [unitsPriceGroup, units, i18n]);
    const [submitted, setSubmitted] = useState(false);
    const [unitSelected, setUnitSelected] = useState(null);
    const [prices, setPrices] = useState([]);
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);

    const getHeaderRow = () => {
        const style = { justifyContent: 'flex-start', px: 2, fontSize: '14px', fontWeight: 'bold' };
        const unitRow = { type: 'header', text: unitSelected?.name, style };

        const headerRow = [
            {
                rowId: 'header',
                cells: [{ type: 'header', text: translate('header_row'), style }, unitRow],
                height: 45,
            },
        ];
        return headerRow;
    };

    const getRows = (values) => {
        const style = { justifyContent: 'flex-start', px: 2, fontSize: '14px', fontWeight: 400 };
        const nonEditable = true;

        return [
            ...getHeaderRow(),
            ...values.map(({ quantityFrom, quantityTo, prices }, index) => {
                let quantityLabel = '';
                if (quantityFrom === quantityTo) {
                    quantityLabel = String(quantityFrom);
                }
                if (!Number.isInteger(quantityTo)) {
                    quantityLabel = `${quantityFrom}+`;
                }
                if (Number.isInteger(quantityTo) && quantityFrom !== quantityTo) {
                    quantityLabel = `${quantityFrom} ${translate('to')} ${quantityTo}`;
                }

                return {
                    rowId: index,
                    cells: [
                        { type: 'text', text: quantityLabel, style, nonEditable },
                        ...prices.map(({ price = null, unitId = null }) => ({
                            type: 'number',
                            value: !Number.isNaN(price) ? price : null,
                            unitId,
                            nonEditable: false,
                        })),
                    ],
                };
            }),
        ];
    };

    // Load ranges
    useEffect(() => {
        if (Array.isArray(rangePrices) && rangePrices?.length > 0) {
            const rangesMap = new Map();

            for (const { quantityFrom, quantityTo } of rangePrices) {
                const key = `${quantityFrom}-${quantityTo}`;

                if (!rangesMap.has(key)) {
                    rangesMap.set(key, { quantityFrom, quantityTo, prices: [{ price: null }] });
                }
            }

            const ranges = [...rangesMap.values()]?.map((range, index) => ({ rowId: index, ...range }));
            setPrices([...ranges]);
        }
    }, [rangePrices]);

    useEffect(() => {
        if (prices?.length > 0 && Number.isFinite(unitSelected?.id)) {
            const newRows = getRows(prices);
            setRows([...newRows]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prices, unitSelected]);

    const handleChangePrices = (changes = [], prevDetails = []) => {
        for (const { rowId, type, newCell } of changes) {
            const dataRow = prevDetails?.find((d) => d?.rowId === rowId);

            if (type === 'number') {
                const { value, unitId } = newCell;

                if (Number.isFinite(value)) {
                    const priceToEdit = dataRow?.prices?.find((price) => price?.unitId === unitId);
                    priceToEdit.price = value;
                }
            }
        }
    };

    const handleChangeDataGrid = (changes) => {
        setPrices((prevPrices) => {
            const updatePrices = [...prevPrices];
            handleChangePrices(changes, updatePrices);

            return updatePrices;
        });
    };

    const handleChangeForm = ({ values }) => {
        const { unitId } = values;
        if (!Number.isFinite(unitId)) return;

        const unitData = units.find((unit) => unit.id === unitId);
        setUnitSelected({ ...unitData });

        const newColumn = { columnId: unitId, width: 200 };

        setColumns([{ ...startColumn }, newColumn]);

        setPrices((prevPrices) => {
            const pricesWithNewUnit = prevPrices.map((range) => ({ ...range, prices: range?.prices?.map((price) => ({ ...price, unitId: unitId })) }));
            return pricesWithNewUnit;
        });
    };

    const handleSubmit = async ({ unitId }) => {
        await addUnit(priceGroupId, unitId, prices);
        handleClose();
    };

    const validateForm = (values) => {
        const errors = {};

        if (!Number.isFinite(values?.unitId)) {
            errors.unitId = translate('validate_form.required');
        }

        const priceErrors = validatePrices(prices, units);

        errors.prices = priceErrors;

        return errors;
    };

    return (
        <Form
            key={`price-group-form-units-${priceGroupId}`}
            onSubmit={handleSubmit}
            validate={validateForm}
            render={({ errors, handleSubmit, form }) => {
                const hasPriceErrors = submitted && errors?.prices;
                const priceErrors = errors?.prices ?? [];

                return (
                    <form key={`price-group-form-units-${priceGroupId}`} onSubmit={handleSubmit} noValidate>
                        <Box component="section" py={2}>
                            <Title title={translate('title')} description={translate('description')} />

                            {/* Form */}
                            <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px">
                                    <Label>{translate('form.unit')}</Label>
                                    <Field name="unitId" component={GroupedSelect} label={translate('form.unit_label')} options={unitsOptions} />
                                </Grid2>

                                <Box width="100%" maxHeight="640px" overflow="auto" mt={1}>
                                    <DataGrid rows={rows} columns={columns} handleChange={handleChangeDataGrid} />
                                </Box>

                                {/* Errors Ranges */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxHeight: '78px',
                                        overflowY: 'auto',
                                        mt: 2,
                                    }}
                                >
                                    {hasPriceErrors && priceErrors?.length > 0 && priceErrors.map((error) => <FormHelperText error={hasPriceErrors}>{error}</FormHelperText>)}
                                </Box>
                            </Grid2>

                            <ActionButtons
                                handleSubmit={(e) => {
                                    setSubmitted(true);
                                    return handleSubmit(e);
                                }}
                                handleClose={(e) => {
                                    handleClose();
                                    return form.reset(e);
                                }}
                                submitting={submittingPriceGroup}
                                labelNext={translate('add_button')}
                            />
                        </Box>

                        {/* Form Spy */}
                        <FormSpy subscription={{ values: true }} onChange={handleChangeForm} />
                    </form>
                );
            }}
        />
    );
};

export default AddUnitForm;
